interface BaseMapper{
  mapTo: (params: any) => any
  mapFrom: (params: any) => any
  mapParamsTo: (params: any) => any
  mapCollectionFrom: (params: any) => any
}

interface BaseAxios{
  post: (...params: any) => any
  get: (...params: any) => any
  put: (...params: any) => any
  delete: (...params: any) => any
}

export interface IUpdateParams {
  id: string;
}

export class BaseHttpTransport<Axios extends BaseAxios, Item, Collection = Item[], CreateItem = Partial<Item>, UpdateItem = Partial<Item> & IUpdateParams, CollectionParams = any, Mapper extends BaseMapper = BaseMapper> {
  constructor(readonly path: string, readonly axios: Axios, readonly mapper?: Mapper) {
  }

  /**
   * Method to add some items
   * @param {CreateItem} data
   * @return {Promise<Item>}
   */
  public add = (data: CreateItem): Promise<Item> => {
    const preparedData = this.mapper && this.mapper.mapTo && typeof this.mapper.mapTo === 'function' ? this.mapper.mapTo(data) : data;
    return this.axios.post(`${this.path}`, preparedData)
      .then((r: any) => {
        return this.mapper && this.mapper.mapFrom && typeof this.mapper.mapFrom === 'function' ? this.mapper.mapFrom(r) : r;
      });
  };

  /**
   * Method to update some items
   * @param {string} id
   * @param {UpdateItem} data
   * @return {Promise<Item>}
   */
  public update = ({ id, ...rest }: Partial<Item> & IUpdateParams): Promise<Item> => {
    const preparedData = this.mapper && this.mapper.mapTo && typeof this.mapper.mapTo === 'function' ? this.mapper.mapTo(rest) : rest;
    return this.axios.put(`${this.path}/${id}`, preparedData)
      .then((r: any) => {
        return this.mapper && this.mapper.mapFrom && typeof this.mapper.mapFrom === 'function' ? this.mapper.mapFrom(r) : r;
      });
  };

  /**
   * Method to delete some item by ID
   * @param {string} id
   * @return {Promise<void>}
   */
  public delete = (id: string): Promise<void> => {
    return this.axios.delete(`${this.path}/${id}`);
  };

  /**
   * Method to trigger delete without params
   * @return {Promise<void>}
   */
  public deleteAll = (): Promise<void> => {
    return this.axios.delete(this.path);
  };

  /**
   * Method to get some item by ID
   * @param {string} id
   * @return {Promise<Item>}
   */
  public get = (id: string): Promise<Item> => {
    return this.axios.get(`${this.path}/${id}`)
      .then((r: any) => {
        return this.mapper && this.mapper.mapFrom && typeof this.mapper.mapFrom === 'function' ? this.mapper.mapFrom(r) : r;
      });
  };

  /**
   * Method to get collection if item by filters <CollectionParams>
   * @param {CollectionParams} params: Filters
   * @return {Promise<Collection>}: Collection of items
   */
  public getCollection = (params: CollectionParams): Promise<Collection> => {
    const preparedParams = this.mapper && this.mapper.mapParamsTo && typeof this.mapper.mapParamsTo === 'function' ? this.mapper.mapParamsTo(params) : params;
    return this.axios.get(`${this.path}${objectToQuery(preparedParams)}`)
      .then((r: any) => {
        return this.mapper && this.mapper.mapCollectionFrom && typeof this.mapper.mapCollectionFrom === 'function' ? this.mapper.mapCollectionFrom(r) : r;
      });
  };
}

export interface IAnyType {
  [key: string]: any;
}
export function objectToQuery(obj: IAnyType): string {
  return obj && typeof obj === 'object'
    ? `?${Object.keys(obj)
      .map(k => {
        const val = obj[k];
        return Array.isArray(val)
          ? obj[k].map((item: any) => `${encodeURIComponent(k)}[]=${encodeURIComponent(item)}`).join('&')
          : `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`;
      })
      .join('&')}`
    : !obj
      ? ''
      : obj;
}
